import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import {
  Box, Fab, Theme, Typography,
  useMediaQuery,
} from '@material-ui/core';
import {
  GetApp as DownloadIcon,
} from '@material-ui/icons';

import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Helmet } from 'react-helmet';
import { decode } from 'hi-base64';

import { Document, Page, pdfjs } from 'react-pdf';

import AppBar from '../../../components/appBar';

import styles from './ResultPreview.styles';
import { RequestAttachPreviewArgs, RequestResultPreviewArgs, ResultPreviewDispatchFunc } from './types';
import ProgressMask from '../../../components/progressMask';
import { ResultDetailsInfo, ResultInfo } from '../resultDetails/types';
import NetworkState from '../../../core/types/network';
import getPersonDisplayName from '../person/utils';

pdfjs.GlobalWorkerOptions.workerSrc = 'pdf.worker.js';

const useStyles = makeStyles(styles);

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

type Props = {
  isPreviewInitialized: boolean;
  resultDetails?: ResultDetailsInfo;
  requestResultPreview: ResultPreviewDispatchFunc<RequestResultPreviewArgs>;
  requestAttachPreview: ResultPreviewDispatchFunc<RequestAttachPreviewArgs>;
  loadedStatus?: any;
  networkStates: {
    resultPreviewRequest: NetworkState;
    attachPreviewRequest: NetworkState;
  };
};

const ResultPreview: React.FC<Props> = ({
  isPreviewInitialized,
  resultDetails,
  requestResultPreview,
  requestAttachPreview,
  loadedStatus,
  networkStates: {
    resultPreviewRequest,
    attachPreviewRequest,
  },
}: Props) => {
  const classes = useStyles();
  const query = useQuery();
  const actionCommand = query.get('action');

  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    if (isPreviewInitialized && actionCommand && resultDetails) {
      const mode = atob(actionCommand)?.split('&')[0];

      if (mode === 'result') {
        const formId = atob(actionCommand)?.split('&')[2];
        const langIndex = atob(actionCommand)?.split('&')[3];

        const formInfo = resultDetails?.resultInfo.find((val) => (
          val.form.formId.toString() === formId));

        const groupTest = resultDetails?.resultInfo.filter(
          (value: ResultInfo) => value.form.formCode === formInfo?.form.formCode,
        );

        if (formInfo) {
          requestResultPreview({
            form: {
              formId: formInfo.form.formId,
              formCode: formInfo.form.formCode,
              formName: formInfo.form.formName,
              formFile: formInfo.form.formFile,
              langIndex,
            },
            requestInfo: resultDetails?.requestInfo,
            resultIds: groupTest?.map((val) => val.id) || [],
          });
        }
      } else if (mode === 'attach') {
        const attachmentId = atob(actionCommand)?.split('&')[2];
        const filePath = decode(atob(actionCommand)?.split('&')[3]);
        const fileType = atob(actionCommand)?.split('&')[4];

        requestAttachPreview({
          requestInfo: resultDetails?.requestInfo,
          attachmentId: btoa(`request_list_result_attachment_id|lab-connect|${attachmentId}`),
          filePath,
          fileType,
        });
      }
    }
  }, [
    actionCommand,
    isPreviewInitialized,
    requestAttachPreview,
    requestResultPreview,
    resultDetails,
  ]);

  const convertError = (code: number) => {
    if (code === 10061) {
      return 'Server Offline. Please Contact Provider';
    }

    return 'Unknow Error';
  };

  const headerText = `${resultDetails?.requestInfo.labNumber || ''} - ${getPersonDisplayName(resultDetails?.requestInfo.patient?.person)}`;

  const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const isMediumScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const documentScale = () => {
    if (isLargeScreen) {
      return 2;
    }

    if (isMediumScreen) {
      return 1.5;
    }

    return 1.2;
  };

  const onDocumentLoadSuccess = (pdf: any) => {
    setNumPages(pdf?.numPages);
  };

  const createPageArray = () => {
    const ret = [];
    for (let index = 1; index <= numPages; index += 1) {
      ret.push(index);
    }

    return ret;
  };

  const downloadPdfFile = (pdf: any) => {
    const file = new Blob([pdf], { type: 'application/pdf;base64' });
    const fileURL = URL.createObjectURL(file);

    const pdfUrl = fileURL;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = headerText;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box className={classNames(classes.root)}>
      <Helmet>
        <title>
          {headerText}
        </title>
      </Helmet>
      <AppBar
        title={headerText}
      />
      {!loadedStatus?.file && (
        <Box className={classes.detailsPanel}>
          {(resultPreviewRequest.isRequesting || attachPreviewRequest.isRequesting) ? (
            <ProgressMask />
          ) : (
            <Typography variant="h5" color="inherit">{convertError(loadedStatus?.error?.code) || 'Please wating...'}</Typography>
          )}
        </Box>
      )}

      {loadedStatus?.file && (
        <Box className={classes.detailsPanelPaper}>
          <Fab
            variant="extended"
            color="secondary"
            className={classes.fabButton}
            onClick={() => downloadPdfFile(loadedStatus?.file)}
          >
            <DownloadIcon className={classes.extendedIcon} />
            Download
          </Fab>
          <Document
            file={{ data: loadedStatus?.file }}
            onLoadError={(error) => error}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            {createPageArray().map((val) => (
              <Page key={val} pageNumber={val} scale={documentScale()} />
            ))}
          </Document>
        </Box>
      )}
    </Box>
  );
};

export default ResultPreview;
